<script setup lang="ts">
import type { TableColumn, TableRow } from '@nuxt/ui'
import type { Invoice, InvoiceStatus } from '@/types'
import { Head, router } from '@inertiajs/vue3'
import { computed, ref } from 'vue'
import { invoiceStatusColor, settlementMonth, statusLabel } from '@/lib/invoices'
import { dashboardLayout } from '@/lib/layouts'
import { formatGrosz } from '@/lib/money'
import { adminInvoicePath } from '@/routes'

defineOptions({
  layout: dashboardLayout,
})

const props = defineProps<{
  invoices: Invoice[]
  customers: { id: number, name: string }[]
  statuses: InvoiceStatus[]
}>()

const title = 'Invoices'
const search = ref('')
const status = ref<'all' | InvoiceStatus>('all')
const month = ref<'all' | string>('all')

const statusItems = computed(() => [
  { label: 'All', value: 'all' },
  ...props.statuses.map(value => ({ label: statusLabel(value), value })),
])

const months = computed(() => {
  const unique = new Set(props.invoices.map(invoice => settlementMonth(invoice.settlement_month)))
  return Array.from(unique).sort().reverse()
})

const monthItems = computed(() => [
  { label: 'All', value: 'all' },
  ...months.value.map(value => ({ label: value, value })),
])

const filtered = computed(() => {
  const query = search.value.trim().toLowerCase()

  return props.invoices.filter((invoice) => {
    const matchesQuery = !query || (invoice.customer_name ?? '').toLowerCase().includes(query)
    const matchesStatus = status.value === 'all' || invoice.status === status.value
    const matchesMonth = month.value === 'all' || settlementMonth(invoice.settlement_month) === month.value

    return matchesQuery && matchesStatus && matchesMonth
  })
})

const columns: TableColumn<Invoice>[] = [
  { accessorKey: 'customer_name', header: 'Customer' },
  { accessorKey: 'settlement_month', header: 'Settlement month' },
  { accessorKey: 'status', header: 'Status' },
  { accessorKey: 'total_amount', header: 'Total' },
  { accessorKey: 'total_quantity', header: 'Usage' },
]

function openInvoice(_event: Event, row: TableRow<Invoice>) {
  router.visit(adminInvoicePath(row.original.id))
}
</script>

<template>
  <Head :title="title" />

  <UDashboardPanel id="invoices">
    <template #header>
      <UDashboardNavbar :title="title">
        <template #leading>
          <UDashboardSidebarCollapse />
        </template>
      </UDashboardNavbar>
    </template>

    <template #body>
      <div class="flex flex-col gap-4">
        <div class="flex flex-wrap items-center justify-between gap-3">
          <div class="flex flex-1 flex-wrap items-center gap-3">
            <UInput
              v-model="search"
              icon="i-lucide-search"
              placeholder="Search by customer…"
              class="w-full max-w-sm"
            />
            <USelect
              v-model="status"
              :items="statusItems"
              value-key="value"
              class="w-40"
            />
            <USelect
              v-model="month"
              :items="monthItems"
              value-key="value"
              class="w-40"
            />
          </div>
          <p class="text-muted shrink-0 text-sm tabular-nums">
            {{ filtered.length }} of {{ invoices.length }}
          </p>
        </div>

        <UTable
          :data="filtered"
          :columns="columns"
          :on-select="openInvoice"
          class="ring-default rounded-lg ring"
          :ui="{ tr: 'cursor-pointer' }"
        >
          <template #customer_name-cell="{ row }">
            <span class="text-highlighted font-medium">{{ row.original.customer_name }}</span>
          </template>

          <template #settlement_month-cell="{ row }">
            <span class="text-muted text-sm">{{ settlementMonth(row.original.settlement_month) }}</span>
          </template>

          <template #status-cell="{ row }">
            <UBadge
              :color="invoiceStatusColor(row.original.status)"
              variant="subtle"
              size="sm"
            >
              {{ statusLabel(row.original.status) }}
            </UBadge>
          </template>

          <template #total_amount-cell="{ row }">
            <span class="tabular-nums">{{ formatGrosz(row.original.total_amount) }}</span>
          </template>

          <template #total_quantity-cell="{ row }">
            <span class="text-muted text-sm tabular-nums">{{ row.original.total_quantity }}</span>
          </template>

          <template #empty>
            <div class="text-muted py-6 text-center text-sm">
              No invoices match your filters.
            </div>
          </template>
        </UTable>
      </div>
    </template>
  </UDashboardPanel>
</template>
